import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { auth, db } from './firebase';
import { BrandingProvider } from './contexts/BrandingContext';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, query, orderBy, doc, getDoc, updateDoc, setDoc, deleteDoc, where } from 'firebase/firestore';
import AdminUsers    from './pages/AdminUsers';
import AdminDashboard from './pages/AdminDashboard';
import CalendarFiltersAdmin from './pages/CalendarFiltersAdmin';
import AdminBranding from './pages/AdminBranding';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DashboardTiles from './pages/DashboardTiles';
import AdminStoreItems from './pages/AdminStoreItems';
import MenuItems from './pages/MenuItems';
import AdminTiles from './pages/AdminTiles';
import MergeAthletes from './pages/MergeAthletes';
import BackfillParents from './pages/BackfillParents';
import LinkCoParents from './pages/LinkCoParents';
import MigrateP2 from './pages/MigrateP2';
import CoParentLink from './pages/CoParentLink';
import Register from './pages/Register';
import People from './pages/People';
import Schedule from './pages/Schedule';
import PublicSchedule from './pages/PublicSchedule';
import QuickCheckIn from './pages/QuickCheckIn';
import CourtRentals from './pages/CourtRentals';
import MembershipTiers from './pages/MembershipTiers';
import AdminGate from './pages/AdminGate';
import Documents from './pages/Documents';
import LeagueRedirect from './pages/LeagueRedirect';
import Financials from './pages/Financials';
import Logs from './pages/Logs';
import TournamentPay from './pages/TournamentPay';
import Payments from './pages/Payments';
import Billing from './pages/Billing';
import CheckIn from './pages/CheckIn';
import Reports from './pages/Reports';
import TrainingPlans from './pages/TrainingPlans';
import PaymentSuccess from './pages/PaymentSuccess';
import Import from './pages/Import';
import AdminSchoolsHub from './pages/AdminSchoolsHub';
import SignContract from './pages/SignContract';
import AthleteProfile from './pages/AthleteProfile';
import Profile from './pages/Profile';
import Welcome from './pages/Welcome';
import NextSteps from './pages/NextSteps';
import Settings from './pages/Settings';
import AddPerson from './pages/AddPerson';
import UserProfile from './pages/UserProfile';
import AdminOverage from './pages/AdminOverage';
import MemberGate from './pages/MemberGate';
import CoachDashboard from './pages/CoachDashboard';
import CoachPayments from './pages/CoachPayments';
import { CircularProgress, Box } from '@mui/material';
import PrivacyPolicy from './pages/PrivacyPolicy';
import NotFound from './pages/NotFound';
import Landing from './pages/Landing';
import PlanRedirect from './pages/PlanRedirect';
import TournamentRedirect from './pages/TournamentRedirect';
import Notifications from './pages/Notifications';
import { useFCM } from './hooks/useFCM';
import TermsOfService from './pages/TermsOfService';
import JuniorTraining from './pages/JuniorTraining';
import AdultTraining from './pages/AdultTraining';
import AdultMembership from './pages/AdultMembership';
import TierRedirect from './pages/TierRedirect';
import DayPassCheckout from './pages/DayPassCheckout';
import DayPassSuccess from './pages/DayPassSuccess';
import TournamentHome     from './pages/TournamentHome';
import TournamentDetail   from './pages/TournamentDetail';
import TournamentRegister from './pages/TournamentRegister';
import TournamentSuccess  from './pages/TournamentSuccess';
import TournamentCancel   from './pages/TournamentCancel';
import TournamentAdminLayout           from './pages/TournamentAdminLayout';
import TournamentAdminDashboard        from './pages/TournamentAdminDashboard';
import TournamentMatchResults          from './pages/TournamentMatchResults';
import TournamentResults                from './pages/TournamentResults';
import TournamentFormPage              from './pages/TournamentForm';
import TournamentAdminRegistrations    from './pages/TournamentAdminRegistrations';
import TournamentRegistrationDetail    from './pages/TournamentRegistrationDetail';
import TournamentAdminFinancial        from './pages/TournamentAdminFinancial';
import TournamentAdminFinancialDetail  from './pages/TournamentAdminFinancialDetail';
import TournamentDivisionManagement    from './pages/TournamentDivisionManagement';
import LeaguesAdmin                    from './pages/LeaguesAdmin';
import LeagueRegistrations             from './pages/LeagueRegistrations';
import LeagueTeams                     from './pages/LeagueTeams';
import LeagueSchedule                  from './pages/LeagueSchedule';
import { LeaguesList, LeagueDetail }   from './pages/Leagues';
import LeagueSignup                   from './pages/LeagueSignup';
import TournamentBrandingSettings      from './pages/TournamentBrandingSettings';
import { TournamentMergeDivisions, TournamentExportCSV } from './pages/TournamentMergeExport';
import Layout from './components/Layout';
import SmsTerms from './pages/SmsTerms';
import SmsOptIn from './pages/SmsOptIn';
import NewsletterSignup from './pages/NewsletterSignup';
import ContactUs from './pages/ContactUs';
import TorchStore from './pages/TorchStore';
import PublicCalendar from './pages/PublicCalendar';
import QuickCheckout from './pages/QuickCheckout';
import Directions from './pages/Directions';
import DocViewer from './pages/DocViewer';
import MyDocuments from './pages/MyDocuments';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Force scroll to top on every route change
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // Also reset any scrollable containers
    const main = document.querySelector('main');
    if (main) main.scrollTop = 0;
  }, [pathname]);
  return null;
}

// Where to land after a successful sign-in: the path the user was originally
// heading for, else their role's default page.
function afterLoginTarget(startPage) {
  const next = new URLSearchParams(window.location.search).get('next');
  // Same-origin app paths only. Rejecting "//host" and absolute URLs keeps this
  // from becoming an open redirect that could bounce a signed-in member to an
  // attacker's page from a crafted link.
  if (next && next.startsWith('/') && !next.startsWith('//')) return next;
  return startPage;
}

// Where to send someone who hits a gated route while signed out.
//
// Previously every gated route redirected to "/", so a link from an email or a
// bookmark dead-ended at the landing page with no explanation and no way back
// to where they were going. Carrying the intended path through login lets it
// resume. Read from window.location at redirect time — this runs during the
// render that performs the redirect, so it is current.
function loginWithReturnPath() {
  const here = `${window.location.pathname}${window.location.search}`;
  // The root is never a return path worth carrying, in any of its forms. Reading
  // window.location means this can fire while a navigation to '/' is in flight
  // and the router is still matching the gated route being left — sign-out does
  // exactly that — and the result was /login?next=%2F%3Fdashboard%3D1: a return
  // path pointing at the public landing page nobody needs to sign in to see.
  const isRoot = here === '/' || here.startsWith('/?');
  return here && !isRoot ? `/login?next=${encodeURIComponent(here)}` : '/login';
}

function RootRedirect({ user, registered, startPage, loading }) {
  const location = useLocation();

  // Wait for Firebase auth to resolve
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  // Not logged in — check for saved default tile, otherwise show Landing
  if (!user) {
    const params = new URLSearchParams(location.search);
    // Invite link (?token=) hit while logged out: stash the token and send them
    // to sign-in. After auth, the !registered branch below routes back to
    // /register?token=… to complete coach onboarding.
    const inviteTok = params.get('token');
    if (inviteTok) {
      sessionStorage.setItem('inviteToken', inviteTok);
      return <Navigate to={`/login?token=${inviteTok}`} replace />;
    }
    // ?dashboard=1 always shows the landing page, no redirect
    if (params.get('dashboard') !== '1') {
      const saved = null; // default-tile feature removed (was localStorage 'tbv_default_tile')
      if (saved) {
        const TILE_PATHS = {
          junior:      '/junior-training',
          adult:       '/adult-training',
          tournaments: '/tournaments',
          membership:  '/adult-membership',
          streaming:   'https://www.sporfie.com/torchvolleyball',
        };
        const path = TILE_PATHS[saved];
        if (path) {
          if (path.startsWith('http')) { window.location.href = path; return null; }
          return <Navigate to={path} replace />;
        }
      }
    }
    return <Landing />;
  }

  // Logged in but not registered — handle invite token then go to register
  if (!registered) {
    // Phase B: if startPage was set to /co-parent-link (caller matched a
    // guardian2Email/Phone of an existing family), route there instead of
    // dropping them into /register. They'll confirm and link, no athlete
    // re-entry needed.
    if (startPage && startPage.startsWith('/co-parent-link')) {
      return <Navigate to={startPage} />;
    }
    const params = new URLSearchParams(location.search);
    const token = params.get('token') || sessionStorage.getItem('inviteToken');
    if (token) sessionStorage.removeItem('inviteToken');
    return <Navigate to={token ? `/register?token=${token}` : '/register'} />;
  }

  // Logged in and registered — ?dashboard=1 shows Landing, otherwise go to startPage
  const params = new URLSearchParams(location.search);
  if (params.get('dashboard') === '1') return <Landing />;
  return <Navigate to={startPage} />;
}

function TournamentWrapper({ user, children, bg }) {
  if (user) {
    return <Layout bg={bg}>{children}</Layout>;
  }
  return <>{children}</>;
}

// Redirect register subdomain at module level (before React hooks)
if (window.location.hostname === 'register.torchvolleyball.com') {
  window.location.replace('https://torchvolleyball.com/tournaments');
}

function App() {
  useFCM();
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registered, setRegistered] = useState(false);
  const [startPage, setStartPage] = useState('/dashboard');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          // Linked login: this uid was merged into another account. Resolve to the
          // canonical member so routing/registration reflect the real account, not
          // this empty stub. (Self-data pages resolve the same pointer.)
          if (data.mergedIntoUid) {
            try {
              const canonSnap = await getDoc(doc(db, 'users', data.mergedIntoUid));
              if (canonSnap.exists()) {
                const canon = canonSnap.data();
                const canonRoles = canon.roles || [];
                const canonStaff = canonRoles.some(r => ['admin', 'coach', 'assistant_coach', 'tournament_admin', 'court_admin', 'member_manager'].includes(r));
                setUser(currentUser);
                setRegistered(!!(canonStaff || canon.registrationComplete || canon.registrationDate));
                setStartPage('/dashboard');
                setLoading(false);
                return;
              }
            } catch (e) { console.error('Alias resolve failed:', e); }
          }
          // Clear pendingLinkEmail if set — user has now signed in
          if (data.pendingLinkEmail) {
            await updateDoc(doc(db, 'users', currentUser.uid), { pendingLinkEmail: false });
          }
          // Sync provider photo on every sign-in. Always refresh from auth.currentUser.photoURL —
          // this catches photo updates the user makes on the Google/Apple side. Also mirror to any
          // matching athlete doc so AthleteProfile and People list stay in sync.
          if (currentUser.photoURL && currentUser.photoURL !== data.photoURL) {
            try {
              await updateDoc(doc(db, 'users', currentUser.uid), { photoURL: currentUser.photoURL });
            } catch (e) { console.warn('User photo sync failed:', e.message); }
          }
          if (currentUser.photoURL && currentUser.email) {
            try {
              const athleteSnap = await getDocs(query(
                collection(db, 'athletes'),
                where('email', '==', currentUser.email.toLowerCase())
              ));
              await Promise.all(athleteSnap.docs.map(async d => {
                if (d.data().photoURL !== currentUser.photoURL) {
                  await updateDoc(d.ref, { photoURL: currentUser.photoURL });
                }
              }));
            } catch (e) { console.warn('Athlete photo mirror failed:', e.message); }
          }
          // If real doc has no roles, check for a pending staff doc to merge from
          if (!(data.roles || []).length && currentUser.email) {
            try {
              const pendingSnap = await getDocs(query(
                collection(db, 'users'),
                where('email', '==', currentUser.email.toLowerCase()),
                where('pendingLinkEmail', '==', true)
              ));
              if (!pendingSnap.empty) {
                const pendingDoc = pendingSnap.docs[0];
                const pendingData = pendingDoc.data();
                await updateDoc(doc(db, 'users', currentUser.uid), {
                  ...pendingData,
                  uid: currentUser.uid,
                  pendingLinkEmail: false,
                  linkedAt: new Date(),
                  ...(currentUser.photoURL && !pendingData.photoURL ? { photoURL: currentUser.photoURL } : {}),
                });
                await deleteDoc(doc(db, 'users', pendingDoc.id));
                const roles = pendingData.roles || [];
                let page = '/dashboard';
                if (roles.some(r => ['coach','assistant_coach'].includes(r)) && !roles.includes('admin')) page = '/coach-dashboard';
                setUser(currentUser);
                // Staff roles count as registered automatically (matches main user-doc path)
                const isStaff = roles.some(r => ['admin', 'coach', 'assistant_coach', 'tournament_admin', 'court_admin', 'member_manager'].includes(r));
                setRegistered(!!(isStaff || pendingData.registrationComplete || pendingData.registrationDate));
                setStartPage(page);
                setLoading(false);
                return;
              }
            } catch (e) {
              console.error('Pending account merge failed:', e);
            }
          }
          const roles = data.roles || [];
          let page = '/dashboard';

          // Phase B gap fix: if this user has signed in before but hasn't
          // completed registration yet, AND another user has them listed as a
          // co-parent (via guardian2Email/Phone), reroute to the co-parent
          // link flow instead of dropping them back into /register. This
          // catches the case where Parent 2 signed in BEFORE Parent 1 added
          // the guardian2 fields — their first sign-in created an empty
          // user doc, so the "no doc by UID" branch below never runs again.
          const isStaff = roles.some(r => ['admin', 'coach', 'assistant_coach', 'tournament_admin', 'court_admin', 'member_manager'].includes(r));
          if (roles.some(r => ['coach','assistant_coach'].includes(r)) && !roles.includes('admin')) page = '/coach-dashboard';
          const isFullyRegistered = isStaff || data.registrationComplete || data.registrationDate || data.pendingLinkEmail;
          if (!isFullyRegistered) {
            try {
              const callerEmail = (currentUser.email || '').toLowerCase().trim();
              const callerPhoneDigits = (currentUser.phoneNumber || '').replace(/\D/g, '');
              const coParentMatches = [];
              if (callerEmail) {
                const emailMatchSnap = await getDocs(query(
                  collection(db, 'users'),
                  where('guardian2Email', '==', callerEmail)
                ));
                emailMatchSnap.docs.forEach(d => coParentMatches.push(d));
              }
              if (callerPhoneDigits) {
                const phoneMatchSnap = await getDocs(query(
                  collection(db, 'users'),
                  where('guardian2Phone', '==', callerPhoneDigits)
                ));
                phoneMatchSnap.docs.forEach(d => {
                  if (!coParentMatches.some(m => m.id === d.id)) coParentMatches.push(d);
                });
              }
              const validMatch = coParentMatches.find(d => d.id !== currentUser.uid && d.data().registrationComplete);
              if (validMatch) {
                setUser(currentUser);
                setRegistered(false);
                setStartPage(`/co-parent-link?match=${validMatch.id}`);
                setLoading(false);
                return;
              }
            } catch (e) {
              console.error('Co-parent re-detection failed:', e);
            }
          }

          // Check for pending plan slug — redirect back to plan page after login
          const pendingPlanSlug = sessionStorage.getItem('pendingPlanSlug');
          if (pendingPlanSlug && (data.registrationComplete || data.registrationDate || data.pendingLinkEmail)) {
            sessionStorage.removeItem('pendingPlanSlug');
            page = `/p/${pendingPlanSlug}`;
          }

          setUser(currentUser);
          // Staff roles count as registered automatically — they don't go through the public registration flow,
          // so requiring registrationComplete/registrationDate would lock them out.
          setRegistered(!!isFullyRegistered);
          setStartPage(page);
        } else {
          // No doc by UID — check for a pending staff account matching this email
          try {
            const pendingSnap = await getDocs(query(
              collection(db, 'users'),
              where('email', '==', currentUser.email.toLowerCase()),
              where('pendingLinkEmail', '==', true)
            ));
            if (!pendingSnap.empty) {
              // Found pending account — migrate it to the real UID
              const pendingDoc = pendingSnap.docs[0];
              const pendingData = pendingDoc.data();
              // Create new doc with real UID
              await setDoc(doc(db, 'users', currentUser.uid), {
                ...pendingData,
                uid: currentUser.uid,
                pendingLinkEmail: false,
                linkedAt: new Date(),
                // Names the doc these roles/membership came from. firestore.rules
                // allows a self-create to carry them only when the named doc
                // exists and its email matches this login's verified email.
                migratedFrom: pendingDoc.id,
                ...(currentUser.photoURL && !pendingData.photoURL ? { photoURL: currentUser.photoURL } : {}),
              });
              // Delete the old pending doc
              await deleteDoc(doc(db, 'users', pendingDoc.id));
              // Set state from linked data
              const roles = pendingData.roles || [];
              let page = '/dashboard';
              if (roles.some(r => ['coach','assistant_coach'].includes(r)) && !roles.includes('admin')) page = '/coach-dashboard';
              setUser(currentUser);
              // Staff roles count as registered automatically (matches main user-doc path)
              const isStaff = roles.some(r => ['admin', 'coach', 'assistant_coach', 'tournament_admin', 'court_admin', 'member_manager'].includes(r));
              setRegistered(!!(isStaff || pendingData.registrationComplete || pendingData.registrationDate));
              setStartPage(page);
              setLoading(false);
              return;
            }
            // No pending doc — check for a mini account from a prior day-pass purchase.
            // Mini accounts use deterministic doc IDs (mini_<emailkey>) and have
            // registrationLevel='mini'. Migrate them into the real UID so the
            // user keeps their purchase history.
            try {
              const miniSnap = await getDocs(query(
                collection(db, 'users'),
                where('email', '==', currentUser.email.toLowerCase()),
                where('registrationLevel', '==', 'mini')
              ));
              // Member shells (a paying member's Stripe subscription, created
              // by createStripeMemberShells) are left for registration's
              // absorbMiniAccount: it re-stamps the subscription to the new
              // account, which this browser-side copy cannot, and without that
              // every renewal keeps writing to the deleted shell.
              const plainMinis = miniSnap.docs.filter(d => !d.data().membership?.stripeSubscriptionId);
              if (plainMinis.length) {
                const miniDoc = plainMinis[0];
                const miniData = miniDoc.data();
                await setDoc(doc(db, 'users', currentUser.uid), {
                  ...miniData,
                  uid: currentUser.uid,
                  registrationLevel: 'full',
                  linkedAt: new Date(),
                  miniMigratedFrom: miniDoc.id,
                  ...(currentUser.photoURL && !miniData.photoURL ? { photoURL: currentUser.photoURL } : {}),
                });
                await deleteDoc(doc(db, 'users', miniDoc.id));
                setUser(currentUser);
                // Mini accounts have minimal data — user still needs full registration to get role-based access.
                setRegistered(false);
                setStartPage('/register');
                setLoading(false);
                return;
              }
            } catch (e) {
              console.error('Mini account merge failed:', e);
            }
            // No pending doc — check for phone match on existing registered accounts
            if (currentUser.phoneNumber) {
              const phoneSnap = await getDocs(query(
                collection(db, 'users'),
                where('phone', '==', currentUser.phoneNumber)
              ));
              const phoneMatch = phoneSnap.docs.find(d =>
                !d.id.startsWith('pending_') && d.data().registrationComplete
              );
              if (phoneMatch) {
                // Used to link the two accounts here, silently and with no
                // confirmation from anybody. It no longer writes, for two
                // reasons.
                //
                // The pointer is server-written now, after a code sent to the
                // other account's address comes back — rules refuse it from a
                // browser, so this write would only ever throw.
                //
                // And it never actually ran: currentUser.phoneNumber is E.164,
                // the query is an exact string match, and one user record out
                // of two hundred is stored that way. A silent account merge
                // that fires for one person in two hundred is worse than one
                // that does not exist.
                //
                // Send them to registration instead, where the same match is
                // found, shown to them, and proved by email before anything is
                // joined.
                setUser(currentUser);
                setRegistered(false);
                setStartPage('/register');
                setLoading(false);
                return;
              }
            }
          } catch (e) {
            console.error('Pending account lookup failed:', e);
          }
          // No existing account match — but maybe they're a co-parent of someone
          // else's family. Check guardian2Email / guardian2Phone for matches.
          // If found, route to /co-parent-link so they can confirm and skip
          // duplicate athlete creation.
          try {
            const callerEmail = (currentUser.email || '').toLowerCase().trim();
            const callerPhoneDigits = (currentUser.phoneNumber || '').replace(/\D/g, '');
            const coParentMatches = [];
            if (callerEmail) {
              const emailMatchSnap = await getDocs(query(
                collection(db, 'users'),
                where('guardian2Email', '==', callerEmail)
              ));
              emailMatchSnap.docs.forEach(d => coParentMatches.push(d));
            }
            if (callerPhoneDigits) {
              const phoneMatchSnap = await getDocs(query(
                collection(db, 'users'),
                where('guardian2Phone', '==', callerPhoneDigits)
              ));
              phoneMatchSnap.docs.forEach(d => {
                if (!coParentMatches.some(m => m.id === d.id)) coParentMatches.push(d);
              });
            }
            // Exclude self-matches and unregistered/archived guardian1 docs.
            const validMatch = coParentMatches.find(d => d.id !== currentUser.uid && d.data().registrationComplete);
            if (validMatch) {
              setUser(currentUser);
              setRegistered(false);
              setStartPage(`/co-parent-link?match=${validMatch.id}`);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error('Co-parent detection failed:', e);
          }
          // Truly new user — preserve returnTo and pendingPlanSlug as postRegReturnTo
          const pendingPlanSlug = sessionStorage.getItem('pendingPlanSlug');
          if (pendingPlanSlug) {
            sessionStorage.setItem('postRegReturnTo', `/p/${pendingPlanSlug}`);
            sessionStorage.removeItem('pendingPlanSlug');
          } else {
            const returnTo = sessionStorage.getItem('returnTo');
            if (returnTo) {
              sessionStorage.setItem('postRegReturnTo', returnTo);
              sessionStorage.removeItem('returnTo');
            }
          }
          setUser(currentUser);
          setRegistered(false);
          // Truly new user: send them to registration (postRegReturnTo above
          // brings them back to where they were shopping afterward). Without
          // this, they fell through to the landing page and login appeared
          // to do nothing.
          setStartPage('/register');
        }
      } else {
        setUser(null);
        setRegistered(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <BrandingProvider>
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<RootRedirect user={user} registered={registered} startPage={startPage} loading={loading} />} />
		<Route path="/my-account" element={ user && registered ? <Profile /> : user && !registered ? <Navigate to="/register" /> : <Login />} />
		<Route path="/billing" element={ user && registered ? <Billing /> : user && !registered ? <Navigate to="/register" /> : <Login />} />
		<Route path="/next-steps" element={ user && registered ? <NextSteps /> : user && !registered ? <Navigate to="/register" /> : <Login />} />
		<Route path="/welcome" element={
		  (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview') === '1' && user) ? <Welcome /> :
		  user && registered ? <Welcome /> :
		  user && !registered ? <Navigate to="/register" /> : <Login />
		} />
		<Route path="/login" element={
		  user && registered ? <Navigate to={afterLoginTarget(startPage)} replace /> :
		  user && !registered && startPage && startPage.startsWith('/co-parent-link') ? <Navigate to={startPage} /> :
		  user && !registered ? <Navigate to="/register" /> :
		  <Login />
		} />
        <Route path="/register" element={user && !registered ? <Register /> : <Navigate to={`/${window.location.search}`} replace />} />
        <Route path="/register-preview" element={user ? <Register /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/dashboard-tiles" element={user && registered ? <DashboardTiles /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/store-items" element={user && registered ? <AdminStoreItems /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/menu-items" element={user && registered ? <MenuItems /> : <Navigate to={loginWithReturnPath()} replace />} />
                <Route path="/admin/admin-tiles" element={user && registered ? <AdminTiles /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/merge-athletes" element={user && registered ? <MergeAthletes /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/overage" element={user && registered ? <AdminOverage /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/gate" element={user && registered ? <MemberGate /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/backfill-parents" element={user && registered ? <BackfillParents /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/link-co-parents" element={user && registered ? <LinkCoParents /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/migrate-p2" element={user && registered ? <MigrateP2 /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/co-parent-link" element={user ? <CoParentLink /> : <Navigate to="/login" />} />
        <Route path="/people" element={user && registered ? <People /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/athletes/:athleteId" element={user && registered ? <AthleteProfile /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/coach-payments" element={user && registered ? <CoachPayments /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/schedule" element={user ? <Schedule /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/calendar" element={<PublicSchedule />} />
        <Route path="/calendar/:slug" element={<PublicSchedule />} />
        <Route path="/event/:eventSlug" element={<PublicSchedule />} />
        <Route path="/quick-checkin" element={<QuickCheckIn />} />
        <Route path="/tournament-pay" element={<TournamentPay />} />
        <Route path="/admin/court-rentals" element={user && registered ? <CourtRentals /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/admin/memberships" element={user && registered ? <MembershipTiers /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/admin/gate" element={user && registered ? <AdminGate /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/admin/documents" element={user && registered ? <Documents /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/plans" element={user && registered ? <Payments /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/checkin" element={user && registered ? <CheckIn /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/reports" element={user && registered ? <Reports /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/training-plans" element={user && registered ? <TrainingPlans /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/buy-sessions" element={<Navigate to="/training-plans" replace />} />
        <Route path="/payment-success" element={user && registered ? <PaymentSuccess /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/import" element={user && registered ? <Import /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/schools" element={user && registered ? <AdminSchoolsHub /> : <Navigate to={loginWithReturnPath()} replace />} />
		{/* Old addresses of the two school pages, now tabs on /admin/schools. Emails already sent link to the first. */}
		<Route path="/admin/school-contracts" element={<Navigate to="/admin/schools?tab=contracts" replace />} />
		<Route path="/admin/school-schedule" element={<Navigate to={`/admin/schools?tab=schedule${window.location.search.replace(/^\?/, '&')}`} replace />} />
        <Route path="/profile" element={<Navigate to="/my-account" replace />} />
        <Route path="/settings" element={user && registered ? <Settings /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/people/new" element={user && registered ? <AddPerson /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/people/:userId" element={user && registered ? <UserProfile /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/coach-dashboard" element={user && registered ? <CoachDashboard /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/p/:slug" element={<PlanRedirect />} />
		<Route path="/m/:slug" element={<TierRedirect />} />
		<Route path="/t/:slug" element={<TournamentRedirect />} />
		<Route path="/l/:slug" element={<LeagueRedirect />} />
		<Route path="/privacy" element={<PrivacyPolicy />} />
		<Route path="/terms" element={<TermsOfService />} />
		<Route path="/junior-training" element={ <TournamentWrapper user={user} bg="#0D1E35"> <JuniorTraining /> </TournamentWrapper>} />
		<Route path="/adult-training" element={ <TournamentWrapper user={user} bg="#0D1E35"> <AdultTraining /> </TournamentWrapper>} />
		<Route path="/adult-membership" element={ <TournamentWrapper user={user} bg="#0D1E35"> <AdultMembership /> </TournamentWrapper>} />
		<Route path="/day-pass-checkout" element={<DayPassCheckout />} />
		<Route path="/day-pass-success" element={<DayPassSuccess />} />
        <Route path="/notifications" element={user && registered ? <Notifications /> : <Navigate to={loginWithReturnPath()} replace />} />
		{/* The page lists tournaments AND leagues, so /events is what it is now
		    called. /tournaments stays as an alias rather than redirecting: it is in
		    sent emails and people's bookmarks, and a link that still works costs
		    nothing next to one that 404s. */}
		<Route path="/events" element={<TournamentWrapper user={user}> <TournamentHome /> </TournamentWrapper>} />
		<Route path="/tournaments" element={<TournamentWrapper user={user}> <TournamentHome /> </TournamentWrapper>} />
		<Route path="/tournament/:tid" element={<TournamentWrapper user={user}> <TournamentDetail /> </TournamentWrapper>} />
		<Route path="/tournament/:tid/results" element={<TournamentWrapper user={user}> <TournamentResults /> </TournamentWrapper>} />
		<Route path="/tournament/:tid/:division" element={ <TournamentWrapper user={user}> <TournamentRegister /> </TournamentWrapper>} />
		<Route path="/tournament-success" element={<TournamentWrapper user={user}> <TournamentSuccess /> </TournamentWrapper>} />
		<Route path="/cancel/:token" element={<TournamentWrapper user={user}> <TournamentCancel /> </TournamentWrapper>} />
        <Route path="/tournament-admin"           element={<TournamentAdminLayout />}>
          <Route index                            element={<TournamentAdminDashboard />} />
          <Route path="tournament/add"            element={<TournamentFormPage />} />
          <Route path="tournament/:tid/edit"      element={<TournamentFormPage />} />
          <Route path="tournament/:tid/match-results" element={<TournamentMatchResults />} />
          <Route path="registrations"             element={<TournamentAdminRegistrations />} />
          <Route path="registration/:rid"         element={<TournamentRegistrationDetail />} />
          <Route path="financial"                 element={<TournamentAdminFinancial />} />
          <Route path="tournament/:tid/financials" element={<TournamentAdminFinancialDetail />} />
          <Route path="divisions"                 element={<TournamentDivisionManagement />} />
          <Route path="leagues"                   element={<LeaguesAdmin />} />
          <Route path="leagues/:leagueId/registrations" element={<LeagueRegistrations />} />
          <Route path="leagues/:leagueId/teams"   element={<LeagueTeams />} />
          <Route path="leagues/:leagueId/schedule" element={<LeagueSchedule />} />
          <Route path="branding"                  element={<TournamentBrandingSettings />} />
          <Route path="merge-divisions"           element={<TournamentMergeDivisions />} />
          <Route path="export-csv"               element={<TournamentExportCSV />} />
        </Route>
		<Route path="/admin" element={user && registered ? <AdminDashboard /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/calendar-filters" element={user && registered ? <CalendarFiltersAdmin /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="/admin/users"    element={user && registered ? <AdminUsers />    : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/branding" element={user && registered ? <AdminBranding /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/membership-report" element={<Navigate to="/reports" replace />} />
		<Route path="/admin/financials" element={user && registered ? <Financials /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/admin/logs" element={user && registered ? <Logs /> : <Navigate to={loginWithReturnPath()} replace />} />
		<Route path="/sms-terms" element={<SmsTerms />} />
		<Route path="/sms-optin" element={<SmsOptIn />} />
		<Route path="/newsletter" element={<NewsletterSignup />} />
		<Route path="/quick-checkout" element={<QuickCheckout />} />
		<Route path="/torch-store" element={<TorchStore />} />
            <Route path="/reservations" element={<PublicCalendar />} />
		<Route path="/contact" element={<ContactUs />} />
		<Route path="/leagues" element={<LeaguesList />} />
		<Route path="/leagues/:leagueId" element={<LeagueDetail />} />
		<Route path="/league-signup" element={<LeagueSignup />} />
		<Route path="/directions" element={<Directions />} />
		<Route path="/docs/:slug" element={<DocViewer />} />
		<Route path="/contract/:token" element={<SignContract />} />
		<Route path="/my-documents" element={user && registered ? <MyDocuments /> : <Navigate to={loginWithReturnPath()} replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
    </BrandingProvider>
  );
}

export default App;
